import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import { PageHeader, PageSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, PrimaryClaim, SecondaryCallout, KMCurve, ComparisonTable, EndpointDisplay, FeaturedResource, TextBlock, NCCNcallout, PageSubSection, PatientProfile, ReferencesBlock } from '../../components';
import { StaticImage } from 'gatsby-plugin-image';
import TNBC from '../../components/StudyDesign/__study-design/tnbc'
import { jobCodes } from '../../util/data/jobCodes';
import { Heading } from '../../util/typography';

// SEO INFORMATION
const pageMeta = {
    title: `KEYNOTE-355 - Clinical Findings & Study Design`,
    keywords: `keynote 355, advanced tnbc clinical trial results`,
    description: `Health care professionals may view findings from KEYNOTE-355 trial, including regimen schedule and overall survival in patients with locally recurrent unresectable or metastatic triple-negative breast cancer (TNBC).`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/triple-negative-breast-cancer/","@type":"ApprovedIndication","name":"KEYTRUDA® and KEYTRUDA QLEX™","alternateName":"pembrolizumab and berahyaluronidase alfa-pmph","description":"KEYTRUDA and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL are each indicated, in combination with chemotherapy, for the treatment of adult patients with locally recurrent unresectable or metastatic triple-negative breast cancer (TNBC) whose tumors express PD-L1 (CPS ≥10) as determined by an FDA-authorized test."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/triple-negative-breast-cancer/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended only for residents of the United States and its territories, including Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/triple-negative-breast-cancer/","@type":"MedicalWebPage","name":"Locally Recurrent Unresectable or Metastatic Triple-Negative Breast Cancer ","description":"KEYTRUDA was studied in combination with paclitaxel, nab-paclitaxel, or gemcitabine + carboplatin for the treatment of locally recurrent unresectable or metastatic TNBC (PD-L1 CPS ≥10)"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/triple-negative-breast-cancer/","@type":"MedicalTrial","name":"KEYNOTE-355","description":"KEYTRUDA was studied with 3 different chemotherapy regimens (paclitaxel, nab-paclitaxel, or gemcitabine and carboplatin) in a multicenter, randomized, double-blind, placebo-controlled trial in 847 patients with locally recurrent unresectable or metastatic TNBC.","trialDesign":"MultiCenterTrial"}`
         ]
    }

//from /src/util/data/indications.js
const indicationId = 26;
const jobCode = jobCodes[26].jobCode;


const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];


const crossLinkContent = {
    sections: [
        {
            heading: "Additional Clinical Data for KEYTRUDA",
            items: [
                { text: 'High-Risk Early-Stage TNBC', url: '/efficacy/early-stage-triple-negative-breast-cancer' },
                { text: '1L Combo therapy – Unresectable Locally Advanced or Metastatic TNBC', url: '/efficacy/metastatic-triple-negative-breast-cancer/' },
            ]
        },
        {
            heading: "Related Information",
            items: [
                { text: 'Selected Adverse Reactions', url: '/safety/adverse-reactions/triple-negative-breast-cancer/' },
                { text: 'Dosing', url: '/dosing/options/#tnbc' },
                { text: 'PD&#8288;-&#8288;L1 Testing & Scoring', url: '/biomarker-testing/pd-l1/' },
            ]
        },
    ],
    interestLinks: {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};

const anchorLinkModelData = [
    { label: 'Clinical Findings from KEYNOTE&#8288;-&#8288;355 for KEYTRUDA' }, 
    { label: 'Study Design for KEYNOTE&#8288;-&#8288;355 (KEYTRUDA)' }, 
    { label: 'NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®)' },
    { label: 'Appropriate Patients' }
]

const DORtableFootnotes = [
    {
        label: "b.",
        text: "Based on the prespecified final analysis."
    },
];

const NCCNcalloutData = {
    showBadge: false,
    bodyCopy: `
        <h4><b>NCCN CATEGORY 1 PREFERRED:</b> Pembrolizumab (KEYTRUDA) as Part of a 1L Treatment Option for Certain Patients With Recurrent Unresectable (Local or Regional) or Stage IV (M1) TNBC (CPS ≥10)<sup>1</sup></h4>
        <p>Pembrolizumab (KEYTRUDA) in combination with chemotherapy (paclitaxel, nab-paclitaxel, or gemcitabine plus carboplatin) for patients with tumor PD-L1 positivity (CPS ≥10)<sup>1</sup></p>
        
    `,
};
const NCCNcalloutDefinitions = `
    <p>Pembrolizumab and berahyaluronidase alfa-pmph subcutaneous injection may be substituted for IV pembrolizumab. Pembrolizumab and berahyaluronidase alfa-pmph has different dosing and administration instructions compared to IV pembrolizumab.</p>
    <p>Category 1 = Based upon high-level evidence (≥1 randomized phase 3 trials or high-quality, robust meta-analyses), there is uniform NCCN consensus (≥85% support of the Panel) that the intervention is appropriate.<sup>1</sup></p>
    <p>According to NCCN, preferred intervention = Intervention that is based on superior efficacy, safety, and evidence; and, when appropriate, affordability.<sup>1</sup></p>
    <p>NCCN makes no warranties of any kind whatsoever regarding their content, use or application and disclaims any responsibility for their application or use in any way.</p>
    <p>1L = first line; IV = intravenous; M1 = distant metastasis.
</p>
`;

// const NCCNcalloutFootnotes = [{
//     label: 'h.',
//     text: 'While available data are in the first-line setting, this regimen can be used for second and subsequent lines of therapy if PD-1/PD-L1 inhibitor therapy has not been previously used. If there is disease progression while on a PD-1/PD-L1 inhibitor, there are no data to support an additional line of therapy with another PD-1/PD-L1 inhibitor.<sup>1</sup>',
// }]

const pageReferences = [
    {
        label: "1.",
        text: "Referenced with permission from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Breast Cancer V.6.2026. © National Comprehensive Cancer Network, Inc. 2026. All rights reserved. Accessed July 29, 2026. To view the most recent and complete version of the guideline, go online to NCCN.org."
    },
];

const patientProfileRegina = {
    image: () => (
        <StaticImage layout="fixed" src="../../assets/patient-tanya.png" placeholder="TRACED_SVG" alt="" width={100} height={100} />
    ),
    name: "Tanya",
    diagnosis: "Stage IV Metastatic TNBC<sup>1</sup>",
    primaryCharacteristics: [
        {
            key: 'Age',
            value: '52',
            emphasize: false,
        },
        {
            key: 'Past Medical and Surgical History',
            value: 'Stage IIA TNBC of the right breast; underwent lumpectomy, surgical axillary staging, and whole breast radiation therapy.',
            emphasize: false,
        },
        {
            key: 'Current Presentation',
            value: 'Palpable mass in the right breast; mammography suggests recurrence of tumor. Biopsy confirmed TNBC.',
            emphasize: false,
        },
        {
            key: 'Current Imaging',
            value: 'CT scan of the chest, abdomen, and pelvis with contrast suggested metastatic disease to the liver; results confirmed by biopsy.',
            emphasize: false,
        },
        {
            key: 'PD-L1 CPS',
            value: '12',
            emphasize: false,
        },
        {
            key: 'ECOG PS',
            value: '0',
            emphasize: false,
        },
    ],
    secondaryCharacteristics: [],
}

const patientDefinitionRegina= "CT = computed tomography; CPS = combined positive score; ECOG PS = European Cooperative Group performance status."

const subHead = "Overall Survival in KEYNOTE&#8288;-&#8288;355";

const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={pageMeta} location={location}>
            <div data-template-name="template-e">

                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader batch2={true} subtitle="For KEYTRUDA" isBranding={true} title="Clinical Trial Results" />
                    <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId} />
                        <AnchorLinks items={anchorLinkModelData} />
                    </PageSection>
                    <PageSection bgColor="gradient">
                        <PrimaryClaim theme="light">KEYTRUDA was studied in combination with paclitaxel, nab-paclitaxel, or gemcitabine + carboplatin for the treatment of locally recurrent unresectable or metastatic TNBC (PD&#8288;-&#8288;L1 CPS ≥10)</PrimaryClaim>
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                    {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection bgColor="cloud" title={anchorLinkModelData[0].label}>
                            <Heading.H4 as="H3" dangerouslySetInnerHTML={{ __html: subHead}} />
                            <SecondaryCallout 
                                title="27% reduced risk of death shown with KEYTRUDA in combination with paclitaxel, nab-paclitaxel, or gem carbo vs paclitaxel, nab-paclitaxel, or gem carbo alone in patients with locally recurrent unresectable or metastatic TNBC whose tumors expressed PD-L1 with a CPS ≥10 <br><br>
                                HR=0.73; 95% CI, 0.55–0.95; <i>P</i>=0.0093<sup>a</sup>"
                            />
                            <KMCurve 
                                title="Kaplan-Meier Estimates for Overall Survival (OS) in KEYNOTE&#8288;-&#8288;355 (PD&#8288;-&#8288;L1 CPS ≥10)" 
                                hr="HR=0.73; 95% CI, 0.55–0.95; <i>P</i>=0.0093<sup>a</sup>" 
                                image={() => <StaticImage src="../../assets/US-OBS-00071_OS-KMC-355.png" placeholder="blurred" alt="Kaplan-Meier Estimates of OS in KEYNOTE-355 (PD-L1 CPS ≥10)" />} 
                            />
                            <TextBlock
                                footnotes={[
                                    {
                                        label: 'a.',
                                        text: 'HR based on stratified Cox regression model. One-sided <i>P</i>-value based on stratified log-rank test (compared to a significance level of 0.0113).',
                                    }
                                ]}
                                definitions={'CI = confidence interval; CPS = combined positive score; gem carbo = gemcitabine and carboplatin; HR = hazard ratio; nab-paclitaxel = paclitaxel protein⁠-⁠bound; Q3W = every 3 weeks; PD-L1 = programmed death ligand 1.'}
                                />
                            <ComparisonTable
                                title="Overall Survival (OS)<sup>b</sup>"
                                footnotes={[
                                    {
                                        label: 'b.',
                                        text: 'Based on the prespecified final analysis.'
                                    }
                                ]}
                                data={
                                [
                                    [
                                        {
                                            legendTitle: 'KEYTRUDA + paclitaxel, nab-paclitaxel, or gem carbo (n=220)',
                                        },
                                        {
                                            legendTitle: 'Placebo + paclitaxel, nab-paclitaxel, or gem carbo (n=103)',
                                        },

                                    ],
                                    [
                                        {
                                            label: 'Events Observed',
                                            dataPoint: '155/220 (70%)',
                                        },
                                        {
                                            label: 'Events Observed',
                                            dataPoint: '84/103 (82%)',
                                        },
                                    ],
                                    [
                                        {
                                            label: 'Median OS',
                                            dataPoint: '23.0 Months',
                                            dataSubPoint: '(95% CI, 19.0–26.3)'
                                        },
                                        {
                                            label: 'Median OS',
                                            dataPoint: '16.1 Months',
                                            dataSubPoint: '(95% CI, 12.6–18.8)'
                                        },
                                    ],
                                ]
                            } />
                            <KMCurve 
                                title="Kaplan-Meier Estimates for Progression-Free Survival (PFS) in KEYNOTE&#8288;-&#8288;355 (PD&#8288;-&#8288;L1 CPS ≥10)" 
                                hr="HR=0.65; 95% CI, 0.49–0.86; <i>P</i>=0.0012<sup>c</sup>" 
                                image={() => <StaticImage src="../../assets/US-OBS-00071-KMC_PFS.png" placeholder="blurred" alt="Kaplan-Meier Estimates of PFS in KEYNOTE-355 (PD-L1 CPS ≥10)" />} 
                            />
                            <SecondaryCallout 
                                title="35% reduction in the risk of disease progression or death shown with KEYTRUDA in combination with paclitaxel, nab-paclitaxel, or gem carbo vs paclitaxel, nab-paclitaxel, or gem carbo alone in patients with tumors that expressed PD&#8288;-&#8288;L1 with a CPS ≥10" text="HR=0.65; 95% CI, 0.49–0.86; <i>P</i>=0.0012<sup>c</sup>" 
                                alt=""
                                footnotes={[
                                    {
                                        label: 'c',
                                        text: 'HR based on stratified Cox regression model. One-sided <i>P</i> value based on stratified log-rank test (compared to a significance level of 0.00411).'
                                    }
                                ]}
                            />
                            <ComparisonTable
                                title="Progression-Free Survival (PFS)<sup>d</sup>"
                                footnotes={[
                                    {
                                        label: 'd.',
                                        text: 'Based on a prespecified interim analysis.'
                                    }
                                ]}
                                data={
                                [
                                    [
                                        {
                                            legendTitle: 'KEYTRUDA + paclitaxel, nab-paclitaxel, or gem carbo (n=220)',
                                        },
                                        {
                                            legendTitle: 'Placebo + paclitaxel, nab-paclitaxel, or gem carbo (n=103)',
                                        },

                                    ],
                                    [
                                        {
                                            label: 'Events Observed',
                                            dataPoint: '136/220 (62%)',
                                        },
                                        {
                                            label: 'Events Observed',
                                            dataPoint: '79/103 (77%)',
                                        },
                                    ],
                                    [
                                        {
                                            label: 'Median PFS',
                                            dataPoint: '9.7 Months',
                                            dataSubPoint: '(95% CI, 7.6–11.3)'
                                        },
                                        {
                                            label: 'Median PFS',
                                            dataPoint: '5.6 Months',
                                            dataSubPoint: '(95% CI, 5.3–7.5)'
                                        },
                                    ],
                                ]
                            } />


                            <EndpointDisplay title="Objective Response Rate (ORR)<sup>b</sup>" limitation="In 220 patients treated with KEYTRUDA + paclitaxel, nab-paclitaxel, or gem carbo vs 103 patients treated with placebo + paclitaxel, nab-paclitaxel, or gem carbo" cards={
                                [
                                    {
                                        dataType: 'ORR',
                                        label: 'KEYTRUDA + paclitaxel, nab-paclitaxel, or gem carbo',
                                        rate: '53%',
                                        ci: '95% CI, 46–59',
                                        n: '116/220',
                                        completeResponseRate: '17%',
                                        partialResponseRate: '35%',
                                    },
                                    
                                ]
                            }
                            />
                            <EndpointDisplay
                                cards={
                                    [
                                        {
                                            dataType: 'ORR',
                                            label: 'Placebo + paclitaxel, nab-paclitaxel, or gem carbo',
                                            rate: '41%',
                                            ci: '95% CI, 31–51',
                                            n: '42/103',
                                            completeResponseRate: '14%',
                                            partialResponseRate: '27%',
                                        }
                                    ]
                                }
                            />

                            <ComparisonTable title="Duration of Response (DOR)<sup>b</sup>" footnotes={DORtableFootnotes}  limitation='Among patients who responded:' data={
                                [
                                    [
                                        {
                                            legendTitle: 'KEYTRUDA + paclitaxel, nab-paclitaxel, or gem carbo (n=116)',
                                        },
                                        {
                                            legendTitle: 'Placebo + paclitaxel, nab-paclitaxel, or gem carbo (n=42)',
                                        },

                                    ],
                                    [
                                        {
                                            label: 'Median DOR',
                                            dataPoint: '12.8 Months',
                                            dataSubPoint: '(95% CI, 9.9–25.9)'
                                        },
                                        {
                                            label: 'Median DOR',
                                            dataPoint: '7.3 Months',
                                            dataSubPoint: '(95% CI, 5.5–15.4)'
                                        }
                                    ],
                                ]
                            } />
                        </PageSection>

                        <PageSection title={anchorLinkModelData[1].label}>
                            <TNBC />
                        </PageSection>

                        <PageSection bgColor="cloud" title="NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®)">
                            <NCCNcallout
                                data={NCCNcalloutData}
                                // footnotes={NCCNcalloutFootnotes} 
                                definitions={NCCNcalloutDefinitions}
                            />
                        </PageSection>
                        <PageSection title={anchorLinkModelData[3].label}>
                            <PageSubSection title="Hypothetical Patient Based on Those Studied in KEYNOTE&#8288;-&#8288;355">
                                <PatientProfile {...patientProfileRegina} 
                                definitions = {patientDefinitionRegina}/>
                            </PageSubSection>
                        </PageSection>
                    </TemplateColumn>
                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>

                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent} />
                    </PageSection>
                    <PageSection bgColor="mist">
                        <ReferencesBlock references={pageReferences} />
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;
